import { Formater } from '@/components/helpers/Financials/helpers';
import { ObjectListItemType } from '@/components/ObjectList/type';
import { formatFullInt, formatPercentToString, verifyNumber } from '@/features/helpers/numbers';
import Portfolios from './types';

const portfolioOverviewDescriptionTuple: Array<
  [keyof Portfolios.PortfolioOverview, string, string, Formater]
> = [
  [
    'potential_market_value',
    'portfolios.overview.potential_market_value',
    'units.market_value',
    formatFullInt as Formater,
  ],
  [
    'potential_net_rent',
    'portfolios.overview.potential_net_rent',
    'units.net_rent',
    formatFullInt as Formater,
  ],
  [
    'potential_charges',
    'portfolios.overview.potential_charges',
    'units.charges',
    formatFullInt as Formater,
  ],
  [
    'potential_roa',
    'portfolios.overview.potential_roa',
    'units.roa',
    formatPercentToString as Formater,
  ],
];

const portfolioOverviewLegendTuple: Array<[keyof Portfolios.PortfolioOverview, string]> = [
  ['total_area', 'surface'],
  ['buildings_nb', 'buildings_nb'],
  ['dwellings_nb', 'dwellings_nb'],
];

export function formatOverview(
  portfolio: Portfolios.PortfolioOverview
): Omit<ObjectListItemType, 'icon'> {
  const { id, label: title, type: subtitle } = portfolio;
  const description = portfolioOverviewDescriptionTuple
    .map(([key, label, suffix, formater]) => {
      const value = verifyNumber(portfolio[key]);
      if (!value) return;

      const diff = verifyNumber(portfolio[(key + '_var') as typeof key]) || null;
      return {
        label,
        trends: [
          {
            value: value ? formater(value) : null,
            diff,
            suffix,
          },
        ],
      };
    })
    .filter(Boolean) as ObjectListItemType['description'];

  const legends = portfolioOverviewLegendTuple
    .map(([key, type]) => {
      const value = verifyNumber(portfolio[key]);
      if (!value) return;
      return { type, value };
    })
    .filter(Boolean) as ObjectListItemType['legends'];

  return {
    id,
    type: 'portfolios',
    title,
    subtitle,
    description,
    legends,
  };
}

export function formatOverviews({
  portfolios,
}: {
  portfolios: Array<Portfolios.PortfolioOverview>;
}): Array<Omit<ObjectListItemType, 'icon'>> {
  return portfolios.map(formatOverview);
}

export function formatTimeSeries<K extends string>(
  series?: Array<Partial<Record<K, number | null>> & { date: string; official_name?: string }>,
  percentageKey?: string[],
  isStep?: boolean
) {
  if (!series || !(series || []).length) return undefined;
  const clearedSeries = series.filter(Boolean);
  const orderedSeries =
    clearedSeries.sort((dataA, dataB) => {
      const dateA = new Date(dataA.date);
      const dateB = new Date(dataB.date);
      if (dateA < dateB) return -1;
      if (dateA > dateB) return 1;
      return 0;
    }) || [];

  const keys = Object.keys(orderedSeries[0]).filter(
    (key) => !['official_name', 'date'].includes(key)
  );
  type MultiLineDataType = Record<
    K,
    Array<{
      group: string;
      xValue: string;
      yValue: number;
      invisible?: boolean;
      name?: string;
    }>
  >;

  const formated = keys.reduce((acc, key) => {
    const currentKeyTimeSeries = orderedSeries
      .map((tsDatePoint) => {
        const { date, official_name, ...rest } = tsDatePoint;
        const datePointValue = rest?.[key as keyof typeof rest];
        if (!datePointValue) return null;
        return {
          name: official_name,
          group: key,
          xValue: date,
          yValue:
            percentageKey?.includes(key) && datePointValue ? datePointValue * 100 : datePointValue,
          invisible: false,
        };
      })
      .filter(Boolean) as Array<{
      name: string;
      group: string;
      xValue: string;
      yValue: number;
      invisible?: boolean;
    }>;

    const duplicated = isStep
      ? currentKeyTimeSeries.flatMap((element, index) =>
          index < currentKeyTimeSeries.length - 1
            ? [
                element,
                { ...element, xValue: currentKeyTimeSeries[index + 1].xValue, invisible: true },
              ]
            : [element, , { ...element, xValue: new Date().toISOString(), invisible: true }]
        )
      : currentKeyTimeSeries;

    return { ...acc, [key]: duplicated };
  }, {} as MultiLineDataType);

  return formated;
}
